import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, X, AlertTriangle, ArrowRight, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const STAGES = ["planned", "in_production", "qc", "completed", "shipped"];
const STATUS_CLS = {
  planned: "bg-navy/10 text-navy/60",
  in_production: "bg-violet/10 text-violet",
  qc: "bg-gold/15 text-gold",
  completed: "bg-emerald-500/10 text-emerald-600",
  shipped: "bg-navy text-cream",
};
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

export default function VettaFactory() {
  const { t, lang } = useI18n();
  const tf = useTeamFilter();
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    api.get("/vetta/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/vetta/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/vetta/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vetta/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (o) => {
    const idx = STAGES.indexOf(o.status);
    if (idx >= STAGES.length - 1) return;
    await api.patch(`/vetta/orders/${o.id}`, { status: STAGES[idx + 1] });
    load();
  };
  const saveStock = async (p, stock) => {
    await api.patch(`/vetta/products/${p.id}`, { stock: Number(stock) || 0 });
    load();
  };
  const money = (n) => `$${Number(n).toLocaleString()}`;

  const [share, setShare] = useState(null);
  const delItem = async (kind, id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/vetta/${kind}/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const [alerting, setAlerting] = useState(false);
  const lowItems = products.filter((p) => p.low);
  const sendAlert = async () => {
    setAlerting(true);
    try {
      const { data } = await api.post("/vetta/reorder/alert");
      if (!data.count) toast(t("os.vetta.reorder.none"));
      else toast.success(`${t("os.vetta.reorder.sent")} · ${t("os.vetta.reorder.count")(data.count)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setAlerting(false); }
  };

  const TABS = ["orders", "timeline", "inventory", "suppliers"];

  return (
    <OSLayout title={t("os.nav.vetta")}>
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">VETTA · Product Intelligence Platform</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Factory</h2>
              <p className="mt-2 text-navy/50">{t("os.vetta.subtitle")}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <OwnerFilter tf={tf} />
              <button data-testid="vetta-new" onClick={() => setModal(tab === "suppliers" ? "supplier" : tab === "inventory" ? "product" : "order")}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {tab === "suppliers" ? t("os.vetta.newSupplier") : tab === "inventory" ? t("os.vetta.newProduct") : t("os.vetta.newOrder")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`vetta-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          <div className="flex gap-2 border-b border-navy/10 overflow-x-auto">
            {TABS.map((tk) => (
              <button key={tk} data-testid={`vetta-tab-${tk}`} onClick={() => setTab(tk)}
                className={`px-4 py-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === tk ? "border-violet text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
                {t(`os.vetta.tabs.${tk}`)}
              </button>
            ))}
          </div>

          {/* Orders */}
          {tab === "orders" && (
            <div className="space-y-3" data-testid="vetta-orders-panel">
              {tf.filter(orders).map((o, i) => (
                <motion.div key={o.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.04 }}
                  data-testid={`vetta-order-${o.id}`} className="bg-white border border-navy/10 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="md:w-40">
                    <div className="font-mono text-sm text-violet">{o.code}</div>
                    <div className="text-xs text-navy/40">{o.due_date}</div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{o.product_name} <span className="text-navy/40">×{o.qty}</span></div>
                    <div className="text-xs text-navy/40">{o.supplier}</div>
                    <div className="mt-2 h-1.5 rounded-full bg-navy/5 overflow-hidden max-w-xs">
                      <div className="h-full bg-gradient-violet" style={{ width: `${o.progress}%` }} />
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_CLS[o.status]}`}>{t(`os.vetta.status.${o.status}`)}</span>
                  {o.status !== "shipped" && (
                    <button data-testid={`vetta-advance-${o.id}`} onClick={() => advance(o)}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-2 hover:border-violet hover:text-violet transition-colors">
                      {t("os.vetta.order.advance")} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <ItemMenu testId={o.id} onInvite={() => setShare({ collection: "vetta_orders", id: o.id, name: o.product_name })}
                    onDelete={() => delItem("orders", o.id, o.product_name)} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Timeline / Gantt */}
          {tab === "timeline" && <TimelinePanel orders={orders} t={t} />}

          {/* Inventory */}
          {tab === "inventory" && (
            <div className="space-y-4" data-testid="vetta-inventory-panel">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-navy/50">
                  {lowItems.length > 0
                    ? <span className="inline-flex items-center gap-1.5 text-gold"><AlertTriangle className="w-4 h-4" /> {t("os.vetta.reorder.count")(lowItems.length)}</span>
                    : <span className="text-navy/40">{t("os.vetta.reorder.none")}</span>}
                </div>
                <button data-testid="vetta-reorder-alert" onClick={sendAlert} disabled={alerting || lowItems.length === 0}
                  className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-40">
                  {alerting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} {t("os.vetta.reorder.btn")}
                </button>
              </div>
              <div className="bg-white border border-navy/10 rounded-lg overflow-hidden">
              <div className="hidden sm:grid grid-cols-[1fr_100px_90px_90px_90px_100px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
                <span>{t("os.vetta.inv.name")}</span><span>{t("os.vetta.inv.sku")}</span><span>{t("os.vetta.inv.stock")}</span>
                <span>{t("os.vetta.inv.price")}</span><span>{t("os.vetta.inv.margin")}</span><span />
              </div>
              {tf.filter(products).map((p) => (
                <div key={p.id} data-testid={`vetta-product-${p.id}`} className="grid grid-cols-2 sm:grid-cols-[1fr_100px_90px_90px_90px_100px] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0">
                  <div className="font-medium flex items-center gap-2">{p.name} {p.low && <AlertTriangle className="w-4 h-4 text-gold" />}</div>
                  <div className="text-xs text-navy/40 font-mono">{p.sku}</div>
                  <input type="number" defaultValue={p.stock} data-testid={`vetta-stock-${p.id}`}
                    onBlur={(e) => e.target.value != p.stock && saveStock(p, e.target.value)}
                    className={`w-20 bg-cream border rounded-lg px-2 py-1.5 outline-none focus:border-violet ${p.low ? "border-gold text-gold" : "border-navy/10"}`} />
                  <div className="text-sm">{money(p.price)}</div>
                  <div className="text-sm text-violet">{p.margin}%</div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs text-navy/40 truncate">{p.supplier}</span>
                    <ItemMenu testId={p.id} onInvite={() => setShare({ collection: "vetta_products", id: p.id, name: p.name })}
                      onDelete={() => delItem("products", p.id, p.name)} />
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}

          {/* Suppliers */}
          {tab === "suppliers" && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vetta-suppliers-panel">
              {tf.filter(suppliers).map((s) => (
                <div key={s.id} data-testid={`vetta-supplier-${s.id}`} className="bg-white border border-navy/10 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">{s.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gold">★ {s.rating}</span>
                      <ItemMenu testId={s.id} onInvite={() => setShare({ collection: "vetta_suppliers", id: s.id, name: s.name })}
                        onDelete={() => delItem("suppliers", s.id, s.name)} />
                    </div>
                  </div>
                  <div className="text-sm text-navy/50 mt-1">{s.country} · {s.category}</div>
                  <div className="mt-4 text-xs text-navy/40">{t("os.vetta.sup.lead")}: {s.lead_time_days}d</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal === "order" && <FormModal title={t("os.vetta.createOrder.title")} testid="vetta-order-modal" onClose={() => setModal(null)}
        fields={[["product_name", t("os.vetta.createOrder.product"), "text", true], ["qty", t("os.vetta.createOrder.qty"), "number", false], ["supplier", t("os.vetta.createOrder.supplier"), "text", false]]}
        submitLabel={t("os.vetta.createOrder.submit")} onSubmit={async (f) => { await api.post("/vetta/orders", { ...f, qty: Number(f.qty) || 1 }); toast.success("OK"); setModal(null); load(); }} />}
      {modal === "supplier" && <FormModal title={t("os.vetta.createSupplier.title")} testid="vetta-supplier-modal" onClose={() => setModal(null)}
        fields={[["name", t("os.vetta.createSupplier.name"), "text", true], ["country", t("os.vetta.createSupplier.country"), "text", false], ["category", t("os.vetta.createSupplier.category"), "text", false]]}
        submitLabel={t("os.vetta.createSupplier.submit")} onSubmit={async (f) => { await api.post("/vetta/suppliers", f); toast.success("OK"); setModal(null); load(); }} />}
      {modal === "product" && <FormModal title={t("os.vetta.createProduct.title")} testid="vetta-product-modal" onClose={() => setModal(null)}
        fields={[["name", t("os.vetta.createProduct.name"), "text", true], ["cost", t("os.vetta.createProduct.cost"), "number", false], ["price", t("os.vetta.createProduct.price"), "number", false], ["stock", t("os.vetta.createProduct.stock"), "number", false], ["reorder_point", t("os.vetta.createProduct.reorder"), "number", false]]}
        submitLabel={t("os.vetta.createProduct.submit")} onSubmit={async (f) => { await api.post("/vetta/products", { ...f, cost: Number(f.cost) || 0, price: Number(f.price) || 0, stock: Number(f.stock) || 0, reorder_point: Number(f.reorder_point) || 20 }); toast.success("OK"); setModal(null); load(); }} />}
      {share && <ShareDialog collection={share.collection} id={share.id} title={share.name} onClose={() => setShare(null)} />}
    </OSLayout>
  );
}

function TimelinePanel({ orders, t }) {
  const STATUS_BAR = {
    planned: "bg-navy/25",
    in_production: "bg-gradient-violet",
    qc: "bg-gold",
    completed: "bg-emerald-500",
    shipped: "bg-navy",
  };
  const parsed = orders
    .map((o) => ({ ...o, start: new Date(o.created_at), end: new Date(o.due_date) }))
    .filter((o) => !isNaN(o.start) && !isNaN(o.end));
  if (!parsed.length) return <div className="text-navy/40" data-testid="vetta-timeline-panel">{t("os.vetta.timeline.empty")}</div>;

  let min = Math.min(...parsed.map((o) => o.start.getTime()));
  let max = Math.max(...parsed.map((o) => o.end.getTime()));
  const today = Date.now();
  min = Math.min(min, today);
  max = Math.max(max, today);
  const span = Math.max(max - min, 1);
  const pct = (ts) => `${((ts - min) / span) * 100}%`;
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-4" data-testid="vetta-timeline-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t("os.vetta.timeline.title")}</h3>
        <div className="text-xs text-navy/40">{fmt(new Date(min))} — {fmt(new Date(max))}</div>
      </div>
      <div className="relative space-y-3 pt-2">
        {/* Today marker */}
        <div className="absolute top-0 bottom-0 w-px bg-violet/60 z-10" style={{ left: pct(today) }}>
          <span className="absolute -top-1 -translate-x-1/2 text-[9px] uppercase tracking-wider text-violet whitespace-nowrap">{t("os.vetta.timeline.today")}</span>
        </div>
        {parsed.map((o, i) => {
          const left = ((o.start.getTime() - min) / span) * 100;
          const width = Math.max(((o.end.getTime() - o.start.getTime()) / span) * 100, 3);
          return (
            <motion.div key={o.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.05 }}
              data-testid={`vetta-timeline-${o.id}`} className="grid grid-cols-[130px_1fr] items-center gap-4">
              <div className="min-w-0">
                <div className="font-mono text-xs text-violet truncate">{o.code}</div>
                <div className="text-xs text-navy/50 truncate">{o.product_name}</div>
              </div>
              <div className="relative h-7">
                <div className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-md ${STATUS_BAR[o.status] || "bg-navy/20"} flex items-center overflow-hidden`}
                  style={{ left: `${left}%`, width: `${width}%` }}>
                  <div className="absolute inset-y-0 left-0 bg-white/25" style={{ width: `${o.progress}%` }} />
                  <span className="relative px-2 text-[10px] text-white whitespace-nowrap">{t(`os.vetta.status.${o.status}`)}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function FormModal({ title, fields, onClose, onSubmit, submitLabel, testid }) {
  const [f, setF] = useState({});
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await onSubmit(f); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid={testid} className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {fields.map(([key, label, type, req]) => (
            <input key={key} required={req} type={type} placeholder={label} data-testid={`vetta-input-${key}`}
              value={f[key] || ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} className={inputCls} />
          ))}
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button data-testid="vetta-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{submitLabel}</button>
        </form>
      </motion.div>
    </div>
  );
}
